import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { GoogleMap, MarkerF } from '@react-google-maps/api';
import type { Park, Review } from '../types';
import { useApp } from '../context/PetParksContext';
import { communityService } from '../services/communityService';
import { Meetup, WeatherData } from '../types/community';
import { WriteReviewModal } from './WriteReviewModal';
import { PhotoUploadModal } from './PhotoUploadModal';
import { ReportContentModal } from './ReportContentModal';
import { CreateMeetupModal } from './CreateMeetupModal';
import { MeetupCard } from './MeetupCard';
import { WeatherWidget } from './WeatherWidget';
import {
  ArrowLeft,
  MapPin,
  Star,
  Clock,
  Shield,
  Navigation,
  Share,
  Heart,
  AlertTriangle,
  Check,
  X,
  ThumbsUp,
  Flag,
  Camera,
  Users,
  Calendar,
  Share2,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { cn } from './ui/utils';
import { toast } from 'sonner';

interface ParkDetailProps {
  park: Park;
  onClose: () => void;
}

export const ParkDetail: React.FC<ParkDetailProps> = ({ park, onClose }) => {
  const navigate = useNavigate();
  const { t, googleMaps } = useApp();
  const [isFavorite, setIsFavorite] = useState(false);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [showCreateMeetup, setShowCreateMeetup] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportContext, setReportContext] = useState<{
    contentId: string;
    contentType: 'review' | 'photo' | 'message';
    preview?: string;
  } | null>(null);

  const [meetups, setMeetups] = useState<Meetup[]>([]);
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [helpfulVotes, setHelpfulVotes] = useState<Record<string, boolean>>({});

  const parkReviews = ([] as Review[]);

  useEffect(() => {
    loadMeetups();
    loadWeather();
  }, [park.id]);

  const loadMeetups = async () => {
    try {
      const data = await communityService.getMeetupsByPark(park.id);
      setMeetups(data);
    } catch (error) {
      console.error('Failed to load meetups:', error);
    }
  };

  const loadWeather = async () => {
    try {
      const data = await communityService.getWeather(
        park.coordinates.lat,
        park.coordinates.lng
      );
      setWeather(data);
    } catch (error) {
      console.error('Failed to load weather:', error);
    }
  };

  const handleReport = (
    contentId: string,
    contentType: 'review' | 'photo',
    preview?: string
  ) => {
    setReportContext({ contentId, contentType, preview });
    setShowReportModal(true);
  };

  const handleHelpfulVote = async (reviewId: string) => {
    try {
      const newState = await communityService.toggleHelpfulVote(
        reviewId,
        'current-user-id'
      );
      setHelpfulVotes((prev) => ({ ...prev, [reviewId]: newState }));
      toast.success(newState ? t('helpfulMarked') : t('voteRemoved'));
    } catch {
      toast.error(t('failedToUpdateVote'));
    }
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: park.name,
          text: t('shareParkText', { parkName: park.name }),
          url: window.location.href,
        })
        .catch(() => {
          // User cancelled share
        });
    } else {
      // Fallback: copy link
      navigator.clipboard.writeText(window.location.href);
      toast.success(t('linkCopiedToClipboard'));
    }
  };

  const handleGetDirections = () => {
    // Open in external maps app
    const destination = `${park.coordinates.lat},${park.coordinates.lng}`;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
      destination
    )}`;
    window.open(mapsUrl, '_blank');
  };

  const AmenityBadge: React.FC<{ available: boolean; label: string }> = ({
    available,
    label,
  }) => (
    <Badge
      variant={available ? 'default' : 'outline'}
      className={cn(
        'text-xs',
        available
          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
          : 'text-muted-foreground'
      )}
    >
      {available ? (
        <Check className="mr-1 h-3 w-3" />
      ) : (
        <X className="mr-1 h-3 w-3" />
      )}
      {label}
    </Badge>
  );

  const ReviewCard: React.FC<{ review: Review }> = ({ review }) => {
    const hasVoted = helpfulVotes[review.id] || false;

    return (
      <Card className="p-4">
        <div className="mb-2 flex items-start justify-between">
          <div>
            <p className="font-medium">{review.userName}</p>
            <div className="mt-1 flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star
                  key={i}
                  className={cn(
                    'h-3.5 w-3.5',
                    i < review.rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300 dark:text-gray-600'
                  )}
                />
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground">{review.date}</p>
        </div>

        <p className="mb-3 text-sm leading-relaxed">{review.content}</p>

        {review.petInfo && (
          <Badge variant="outline" className="mb-3 text-xs">
            🐕 {review.petInfo.name} • {review.petInfo.size}
          </Badge>
        )}

        <div className="flex items-center gap-4 text-xs">
          <button
            type="button"
            className={cn(
              'flex items-center gap-1 transition-colors cursor-pointer',
              hasVoted
                ? 'text-primary font-medium'
                : 'text-muted-foreground hover:text-foreground'
            )}
            onClick={() => handleHelpfulVote(review.id)}
          >
            <ThumbsUp
              className={cn('h-3.5 w-3.5', hasVoted && 'fill-primary')}
            />
            {t('helpful')} ({review.helpful + (hasVoted ? 1 : 0)})
          </button>
          <button
            type="button"
            className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-foreground cursor-pointer"
            onClick={() => handleReport(review.id, 'review', review.content)}
          >
            <Flag className="h-3.5 w-3.5" />
            {t('report')}
          </button>
        </div>
      </Card>
    );
  };

  return (
    <div className="flex h-full max-h-full flex-col overflow-hidden bg-background">
      {/* Header */}
      <div className="flex items-center gap-2 border-b bg-card p-3 sm:gap-3 sm:p-4 flex-shrink-0">
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-9 w-9 sm:h-10 sm:w-10 cursor-pointer"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h2 className="flex-1 text-base font-semibold sm:text-lg">
          {park.name}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setIsFavorite(!isFavorite)}
          className="h-9 w-9 sm:h-10 sm:w-10 cursor-pointer"
        >
          <Heart
            className={cn(
              'h-5 w-5',
              isFavorite ? 'fill-red-500 text-red-500' : ''
            )}
          />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 sm:h-10 sm:w-10 cursor-pointer"
          onClick={handleShare}
        >
          <Share className="h-5 w-5" />
        </Button>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="space-y-4 p-3 sm:space-y-6 sm:p-4">
          {/* Hero Image Placeholder */}
          <div className="relative h-40 overflow-hidden rounded-lg bg-gradient-to-br from-primary/20 to-accent sm:h-48">
            {googleMaps.apiKey &&
            googleMaps.isLoaded &&
            !googleMaps.loadError ? (
              <div className="absolute inset-0">
                {/* Inline map preview for the selected park */}
                <GoogleMap
                  center={park.coordinates}
                  zoom={15}
                  mapContainerClassName="h-full w-full"
                  options={{
                    clickableIcons: false,
                    disableDefaultUI: true,
                    gestureHandling: 'none',
                  }}
                >
                  <MarkerF position={park.coordinates} />
                </GoogleMap>
              </div>
            ) : (
              <div className="flex h-full items-center justify-center">
                <MapPin className="h-12 w-12 text-primary/40 sm:h-16 sm:w-16" />
              </div>
            )}
            {park.verified && (
              <Badge className="absolute right-2 top-2 bg-white/90 text-primary dark:bg-black/70 sm:right-3 sm:top-3">
                <Shield className="mr-1 h-3 w-3" />
                {t('verifiedBadge')}
              </Badge>
            )}

            {/* Photo Upload & Share to Social */}
            <div className="absolute bottom-2 right-2 flex flex-col gap-2 sm:bottom-3 sm:right-3 sm:flex-row">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setShowPhotoUpload(true)}
              >
                <Camera className="mr-2 h-4 w-4" />
                {t('addPhotos')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  navigate({
                    to: '/dashboard/social',
                    search: { create: true, park: park.name },
                  })
                }
              >
                <Share2 className="mr-2 h-4 w-4" />
                {t('shareToSocial')}
              </Button>
            </div>
          </div>

          {/* Weather Widget */}
          {weather && <WeatherWidget weather={weather} />}

          {/* Basic Info */}
          <div>
            <div className="mb-3 flex flex-wrap items-center gap-2 sm:gap-3">
              <div className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400 sm:h-5 sm:w-5" />
                <span className="text-base font-semibold sm:text-lg">
                  {park.rating.toFixed(1)}
                </span>
                <span className="text-xs text-muted-foreground sm:text-sm">
                  ({park.reviewCount} {t('reviews')})
                </span>
              </div>
              <Badge
                variant={park.isOpen ? 'default' : 'secondary'}
                className={cn(
                  'text-xs',
                  park.isOpen
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                    : ''
                )}
              >
                {park.isOpen ? t('openNow') : t('closed')}
              </Badge>
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-start gap-2">
                <MapPin className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
                <div>
                  <p className="leading-relaxed">{park.address}</p>
                  <p className="text-muted-foreground">
                    {park.distance} {t('kmAway')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <p>{park.hours}</p>
              </div>
            </div>

            <div className="mt-4">
              <Button
                className="w-full sm:w-auto cursor-pointer"
                onClick={handleGetDirections}
              >
                <Navigation className="mr-2 h-4 w-4" />
                {t('directions')}
              </Button>
            </div>
          </div>

          <Separator />

          {/* Tabs for organized content */}
          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="overview">{t('overview')}</TabsTrigger>
              <TabsTrigger value="reviews">{t('reviews')}</TabsTrigger>
              <TabsTrigger value="meetups">
                {t('meetups')}
                {meetups.length > 0 && (
                  <Badge
                    variant="secondary"
                    className="ml-2 h-5 min-w-5 rounded-full px-1.5 text-xs"
                  >
                    {meetups.length}
                  </Badge>
                )}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-4 pt-4">
              {/* Description */}
              <div>
                <h3 className="mb-2 text-base font-semibold">{t('about')}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {park.description}
                </p>
              </div>

              {/* Alerts */}
              {park.alerts.length > 0 && (
                <div>
                  <h3 className="mb-3 text-base font-semibold">
                    {t('recentAlerts')}
                  </h3>
                  <div className="space-y-2">
                    {park.alerts.map((alert) => (
                      <div
                        key={alert.id}
                        className={cn(
                          'rounded-md p-3',
                          alert.type === 'warning' &&
                            'bg-yellow-50 dark:bg-yellow-900/20',
                          alert.type === 'danger' &&
                            'bg-red-50 dark:bg-red-900/20',
                          alert.type === 'info' &&
                            'bg-blue-50 dark:bg-blue-900/20'
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <AlertTriangle
                            className={cn(
                              'mt-0.5 h-4 w-4 flex-shrink-0',
                              alert.type === 'warning' &&
                                'text-yellow-600 dark:text-yellow-500',
                              alert.type === 'danger' &&
                                'text-red-600 dark:text-red-500',
                              alert.type === 'info' &&
                                'text-blue-600 dark:text-blue-500'
                            )}
                          />
                          <div className="flex-1">
                            <p className="text-sm font-medium">
                              {alert.message}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              {alert.date}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Amenities */}
              <div>
                <h3 className="mb-3 text-base font-semibold">
                  {t('amenities')}
                </h3>
                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  <AmenityBadge
                    available={park.waterFountain}
                    label={t('waterFountain')}
                  />
                  <AmenityBadge
                    available={park.wasteBags}
                    label={t('wasteBags')}
                  />
                  <AmenityBadge available={park.benches} label={t('benches')} />
                  <AmenityBadge available={park.shade} label={t('shade')} />
                  <AmenityBadge available={park.parking} label={t('parking')} />
                  <AmenityBadge
                    available={park.agilityEquipment}
                    label={t('agilityEquipment')}
                  />
                  <AmenityBadge
                    available={park.fullyFenced}
                    label={t('fullyFenced')}
                  />
                  <AmenityBadge
                    available={park.separateAreas}
                    label={t('separateAreas')}
                  />
                  <AmenityBadge
                    available={park.offLeash}
                    label={t('offLeash')}
                  />
                </div>
              </div>

              {/* Rules */}
              <div>
                <h3 className="mb-3 text-base font-semibold">
                  {t('rulesRegulations')}
                </h3>
                <ul className="space-y-2">
                  {park.rules.map((rule, index) => (
                    <li
                      key={index}
                      className="flex gap-2 text-sm leading-relaxed"
                    >
                      <span className="text-primary">•</span>
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Hazards */}
              {park.hazards.length > 0 && (
                <div>
                  <h3 className="mb-3 text-base font-semibold">
                    {t('knownHazards')}
                  </h3>
                  <ul className="space-y-2">
                    {park.hazards.map((hazard, index) => (
                      <li
                        key={index}
                        className="flex gap-2 text-sm leading-relaxed"
                      >
                        <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-500" />
                        <span>{hazard}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </TabsContent>

            <TabsContent value="reviews" className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">
                  {t('reviews')} ({parkReviews.length})
                </h3>
                <Button size="sm" onClick={() => setShowWriteReview(true)}>
                  {t('writeReview')}
                </Button>
              </div>

              {parkReviews.length === 0 ? (
                <Card className="p-6 text-center sm:p-8">
                  <Star className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                  <p className="mb-2 font-medium">{t('noReviews')}</p>
                  <p className="mb-4 text-sm text-muted-foreground">
                    {t('beFirst')}
                  </p>
                  <Button size="sm" onClick={() => setShowWriteReview(true)}>
                    {t('writeFirstReview')}
                  </Button>
                </Card>
              ) : (
                <div className="space-y-3">
                  {parkReviews.map((review) => (
                    <ReviewCard key={review.id} review={review} />
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="meetups" className="space-y-4 pt-4">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-semibold">
                  {t('upcomingMeetups')}
                </h3>
                <Button size="sm" onClick={() => setShowCreateMeetup(true)}>
                  <Users className="mr-2 h-4 w-4" />
                  {t('organize')}
                </Button>
              </div>

              {meetups.length === 0 ? (
                <Card className="p-6 text-center sm:p-8">
                  <Calendar className="mx-auto mb-3 h-12 w-12 text-muted-foreground" />
                  <p className="mb-2 font-medium">{t('noUpcomingMeetups')}</p>
                  <p className="mb-4 text-sm text-muted-foreground">
                    {t('beFirstOrganizeMeetup')}
                  </p>
                  <Button size="sm" onClick={() => setShowCreateMeetup(true)}>
                    <Users className="mr-2 h-4 w-4" />
                    {t('createMeetup')}
                  </Button>
                </Card>
              ) : (
                <div className="space-y-3">
                  {meetups.map((meetup) => (
                    <MeetupCard key={meetup.id} meetup={meetup} />
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* Modals */}
      <WriteReviewModal
        park={park}
        open={showWriteReview}
        onClose={() => setShowWriteReview(false)}
        onSuccess={loadMeetups}
      />

      <PhotoUploadModal
        park={park}
        open={showPhotoUpload}
        onClose={() => setShowPhotoUpload(false)}
      />

      <CreateMeetupModal
        park={park}
        open={showCreateMeetup}
        onClose={() => setShowCreateMeetup(false)}
        onSuccess={loadMeetups}
      />

      {reportContext && (
        <ReportContentModal
          open={showReportModal}
          onClose={() => {
            setShowReportModal(false);
            setReportContext(null);
          }}
          contentType={reportContext.contentType}
          contentId={reportContext.contentId}
          contentPreview={reportContext.preview}
        />
      )}
    </div>
  );
};
